import React, { forwardRef, type RefObject, useEffect, useState } from "react";
import {
  type NativeSyntheticEvent,
  Platform,
  type StyleProp,
  TextInput,
  type TextInputKeyPressEventData,
  type TextInputProps,
  type TextStyle,
  View,
  type ViewStyle,
} from "react-native";

import { isIOS, isWeb } from "@stepapp/core";

type Props = TextInputProps & {
  inputContainerStyles?: StyleProp<ViewStyle>;
  firstInput: boolean;
  focusStyles?: StyleProp<ViewStyle>;
  inputStyles?: StyleProp<TextStyle>;
  numberOfInputs: number;
  handleTextChange: (text: string) => void;
  inputValue: string;
  handleKeyPress: (keyPressEvent: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
};

const majorVersionIOS: number = Number.parseInt(`${Platform.Version}`, 10);
const isOTPSupported: boolean = isIOS && majorVersionIOS >= 12;

const OtpInputComponent = forwardRef<TextInput, Props>(
  (
    {
      autoFocus,
      focusStyles,
      handleKeyPress,
      handleTextChange,
      inputContainerStyles,
      inputStyles,
      inputValue,
      placeholder,
      selectTextOnFocus,
      secureTextEntry,
      ...rest
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(false);

    useEffect(() => {
      (ref as RefObject<TextInput>)?.current?.setNativeProps?.({
        value: inputValue,
        text: inputValue,
        secureTextEntry,
      });
    }, [ref, inputValue, secureTextEntry]);

    return (
      <View style={[inputContainerStyles, focused && focusStyles]}>
        <TextInput
          {...rest}
          autoFocus={autoFocus}
          onBlur={() => setFocused(false)}
          onChangeText={handleTextChange}
          onFocus={() => setFocused(true)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          ref={ref}
          secureTextEntry={secureTextEntry}
          selectTextOnFocus={Platform.select({ ios: selectTextOnFocus, android: true })}
          style={inputStyles}
          textContentType={isOTPSupported ? "oneTimeCode" : "none"}
          underlineColorAndroid="transparent"
          value={isWeb ? inputValue : undefined}
        />
      </View>
    );
  },
);

export default React.memo(OtpInputComponent);
